import endpoints from '@app/src/api/endpoints'
import { FetchKey } from '@app/src/api/fetchHooks'
import { useUpdateResource } from '@app/src/api/updateHooks'
import { useDrawer } from '@app/src/components/Drawer/DrawerContext'
import type { DrawerViewProps } from '@app/src/components/Drawer/DrawerView'
import DrawerView from '@app/src/components/Drawer/DrawerView'
import type { Option } from '@app/src/components/Form/Select'
import Select from '@app/src/components/Form/Select/ControlledSelect'
import TextField from '@app/src/components/Ui/TextField'
import { useLocale } from '@app/src/context/LocaleContext'
import { useSnackbar } from '@app/src/context/SnackbarContext'
import useErrorNotification from '@app/src/hooks/errorNotification'
import useLocalisedCountryName from '@app/src/hooks/useLocalisedCountryName'
import { RequestAutomationType } from '@app/src/pages/Configurations/ConfigurationsPages/Automation/RequestAutomationType'
import RequestAutomationWarningAlert from '@app/src/pages/Configurations/ConfigurationsPages/Automation/Warning/RequestAutomationWarningAlert'
import { SortOrder } from '@app/src/types/filter'
import type { Provider } from '@app/src/types/organizations'
import type { Country } from '@app/src/types/resourceExplorer'
import { NotificationSeverity, ORGANIZATION_NAME_REGEX } from '@app/src/wf-constants'
import { Stack, Typography } from '@mui/material'
import { useQueryClient } from '@tanstack/react-query'
import { capitalize } from 'lodash'
import React from 'react'
import { useForm } from 'react-hook-form'
import { useIntl } from 'react-intl'

type DrawerViewAddProviderPlaceholderProps = {
  providerType: string
} & Omit<DrawerViewProps, 'title'>

const DrawerViewAddProviderPlaceholder: React.FC<DrawerViewAddProviderPlaceholderProps> = ({
  providerType,
  ...props
}) => {
  const { formatMessage } = useIntl()
  const { closeDrawer } = useDrawer()
  const { showSnackbar } = useSnackbar()
  const { showErrorNotification } = useErrorNotification()
  const { register, errors, setValue, control, handleSubmit, setError } = useForm<Partial<Provider>>({
    mode: 'onChange',
  })
  const queryClient = useQueryClient()
  const { mutateAsync, isPending: isLoading } = useUpdateResource<Partial<Provider>>()
  const { getLocalisedCountryName } = useLocalisedCountryName()
  const { locale } = useLocale()

  const onSubmit = async (values: Partial<Provider>): Promise<void> => {
    await mutateAsync(
      {
        body: { ...values, name: values.name?.trim() },
        url: endpoints.saveProvider,
      },
      {
        onSuccess: () => {
          showSnackbar({
            message: formatMessage(
              { id: 'notifications.successfulProviderSave' },
              { provider: formatMessage({ id: `general.${capitalize(providerType)}` }, { count: 1 }) },
            ),
            severity: NotificationSeverity.success,
            disableAutoClose: true,
          })
          queryClient.invalidateQueries({ queryKey: [FetchKey.ProviderCollection] })
          setTimeout(() => {
            queryClient.invalidateQueries({ queryKey: [FetchKey.JobRunning, 'enrichment'] })
            queryClient.invalidateQueries({ queryKey: [FetchKey.JobRunning, 'screening'] })
          }, 1000)
          closeDrawer()
        },
        onError: error => {
          if (error.isValidationError) {
            error.setFormValidationErrors(setError)
            return
          }
          showErrorNotification({ requestError: error, disableAutoClose: true })
        },
      },
    )
  }

  return (
    <DrawerView
      title={formatMessage(
        { id: 'resourceCollections.create.createProviderPlaceholder' },
        { provider: formatMessage({ id: `general.${capitalize(providerType)}` }, { count: 1 }).toLowerCase() },
      )}
      buttons={[
        {
          label: formatMessage(
            { id: 'resourceCollections.create.createProvider' },
            { provider: formatMessage({ id: `general.${capitalize(providerType)}` }, { count: 1 }).toLowerCase() },
          ),
          variant: 'contained',
          type: 'submit',
          loading: isLoading,
        },
      ]}
      onFormSubmit={handleSubmit(onSubmit)}
      {...props}
    >
      <Stack px={2} spacing={2}>
        <Typography paragraph>
          {formatMessage(
            { id: 'resourceCollections.create.createProviderPlaceholderSubtitle' },
            { provider: formatMessage({ id: `general.${capitalize(providerType)}` }, { count: 2 }).toLowerCase() },
          )}
        </Typography>

        <TextField
          hoveringLabel
          placeholder={formatMessage({ id: 'textFieldPlaceholders.organizationName' })}
          inputRef={register({
            required: formatMessage({ id: 'form.validation.required' }),
            minLength: {
              value: 2,
              message: formatMessage({ id: 'errorMessages.general.minLength' }, { count: 2 }),
            },
            pattern: {
              value: ORGANIZATION_NAME_REGEX,
              message: formatMessage({ id: 'form.validation.latinCharactersOnly' }),
            },
          })}
          fullWidth
          required
          name="name"
          label={formatMessage({ id: 'schemas.organization.name' })}
          error={Boolean(errors?.name)}
          type="text"
          helperText={errors?.name?.message}
          onClear={(): void => setValue('name', '')}
        />

        <Stack translate="no" className="notranslate">
          <Select<number, Country>
            key={locale}
            hoveringLabel
            variant="outlined"
            name="countryId"
            rules={{ required: 'This field is required' }}
            fieldLabel={formatMessage({ id: 'schemas.organization.country' })}
            control={control}
            required
            forceFetch
            navigation={{
              url: endpoints.countries,
              type: 'post',
              postObject: {
                pagination: {
                  itemsPerPage: 9999,
                  pageNumber: 1,
                },
                include: [],
                filter: [],
                sort: { target: 'name', order: SortOrder.ASCENDING },
              },
            }}
            objectToOption={(country: Country): Option<number> => {
              return {
                label: getLocalisedCountryName(country.countryCode),
                value: country.id,
              }
            }}
            error={errors?.countryId?.message}
          />
        </Stack>

        <TextField
          hoveringLabel
          placeholder={formatMessage({ id: 'textFieldPlaceholders.website' })}
          fullWidth
          name="websiteAddress"
          inputRef={register()}
          label={formatMessage({ id: 'schemas.organization.websiteAddress' })}
          error={Boolean(errors?.websiteAddress)}
          type="text"
          helperText={errors?.websiteAddress?.message}
          onClear={(): void => setValue('websiteAddress', '')}
        />

        <Stack mt={2} spacing={2}>
          <RequestAutomationWarningAlert
            automationType={RequestAutomationType.AI_ENRICH}
            providerType={providerType}
            closeDrawer={closeDrawer}
          />
          <RequestAutomationWarningAlert
            automationType={RequestAutomationType.AI_SCREEN}
            providerType={providerType}
            closeDrawer={closeDrawer}
          />
        </Stack>
      </Stack>
    </DrawerView>
  )
}

export default DrawerViewAddProviderPlaceholder
